import { useState } from "react";
import {
  useDeleteSnapshot,
  useReportSnapshots,
  type SnapshotSummary,
} from "@/api/queries";
import { Chip } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { CalendarIcon, CameraIcon, TrashIcon } from "@/components/icons";
import { useFormatter } from "@/artifacts/format";
import { useRelativeTime } from "@/lib/time";

/**
 * A report's snapshot history, and the only way to remove one (PRD FR-7.5).
 *
 * `DELETE /api/snapshots/{id}` has existed since M6 and nothing called it: a
 * snapshot could be taken from the report header or by a schedule, and then
 * never removed — so a mistimed capture, or a run against a half-loaded
 * warehouse, sat in the trend forever bending the line it was supposed to
 * explain.
 *
 * It lives under the Trend tab because that is where snapshots are the
 * subject, and it renders **beside** the trend rather than inside it: a report
 * whose tiles are all tables has snapshots and no trendable metric, and the
 * history is exactly what that reader needs to see.
 *
 * Deleting one is destructive in the way a tile is not — a snapshot is the
 * only record of what the numbers were at that moment, and nothing upstream
 * can recompute it. Hence the confirmation, and hence the row states what the
 * capture contained rather than only when it happened.
 */
export function SnapshotHistory({ reportId, enabled }: { reportId: string; enabled: boolean }) {
  const { data: snapshots, isLoading } = useReportSnapshots(reportId, enabled);
  const remove = useDeleteSnapshot(reportId);
  const [confirming, setConfirming] = useState<SnapshotSummary | null>(null);
  // `MING_LOCALE`, the same setting money is formatted in — never the
  // browser's, which would make one deployment read two ways.
  const { locale } = useFormatter();

  if (isLoading) {
    return <div className="bg-muted rounded-container h-24 animate-pulse" />;
  }
  if (!snapshots?.length) return null;

  return (
    <section className="border-border flex flex-col gap-2 border-t pt-4">
      <div className="flex items-center gap-2">
        <h3 className="type-label text-ink">History</h3>
        <Chip tone="neutral">
          {snapshots.length} snapshot{snapshots.length === 1 ? "" : "s"}
        </Chip>
      </div>
      <ul className="flex flex-col">
        {snapshots.map((snapshot) => (
          <SnapshotRow
            key={snapshot.id}
            snapshot={snapshot}
            onRemove={() => setConfirming(snapshot)}
          />
        ))}
      </ul>

      {/* One dialog for the list, as the conversation sidebar does: the row
          being deleted is named in it, so there is nothing to keep in sync. */}
      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        loading={remove.isPending}
        title="Delete this snapshot?"
        description={
          <>
            The capture from {confirming ? absolute(confirming.captured_at, locale) : ""} will be
            deleted.
          </>
        }
        consequence="A snapshot is the only record of what these values were at that moment — nothing can recompute it. The trend and Cause Lens lose that point."
        onConfirm={() => {
          const target = confirming;
          if (!target) return;
          remove.mutate(target.id, { onSuccess: () => setConfirming(null) });
        }}
      />
    </section>
  );
}

function SnapshotRow({
  snapshot,
  onRemove,
}: {
  snapshot: SnapshotSummary;
  onRemove: () => void;
}) {
  const stamp = useRelativeTime(snapshot.captured_at);
  const { locale } = useFormatter();
  return (
    <li className="border-border/60 hover:bg-hover motion-fast group flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-2 last:border-b-0">
      {snapshot.trigger === "schedule" ? (
        <CalendarIcon className="text-ink-disabled size-3.5 shrink-0" />
      ) : (
        <CameraIcon className="text-ink-disabled size-3.5 shrink-0" />
      )}
      <span className="type-support text-ink" title={absolute(snapshot.captured_at, locale)}>
        {stamp}
      </span>
      <span className="type-caption text-ink-disabled">
        {snapshot.trigger === "schedule" ? "by schedule" : "taken by hand"} ·{" "}
        {snapshot.tile_count} tile{snapshot.tile_count === 1 ? "" : "s"} ·{" "}
        {snapshot.metrics.length} metric{snapshot.metrics.length === 1 ? "" : "s"}
      </span>
      {snapshot.failure_count ? (
        <Chip tone="error">
          {snapshot.failure_count} failed
        </Chip>
      ) : null}
      <span className="flex-1" />
      <button
        type="button"
        aria-label={`Delete the snapshot from ${absolute(snapshot.captured_at, locale)}`}
        onClick={onRemove}
        className="text-ink-disabled hover:text-error hover:bg-hover rounded-inner motion-fast p-1.5 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
      >
        <TrashIcon className="size-3.5" />
      </button>
    </li>
  );
}

/**
 * The exact moment, for the tooltip and for the dialog that names it.
 *
 * A relative stamp is right for "how fresh is this" and wrong for "which one
 * am I about to delete" — two captures an hour apart are both "2 days ago".
 */
function absolute(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date);
}
